import {MemexColumnDataType} from '../api/columns/contracts/memex-column'
import type {CustomColumnKind} from '../api/columns/contracts/storage'
import {ItemType} from '../api/memex-items/item-type'

/**
 * Defines the capabilities a column can have.
 *
 * This is a work in progress - eventually all column capabilities such as
 * grouping, sorting, filtering, and other functionality should be centrally
 * located here rather than spread across multiple configuration files.
 *
 * When adding a new capability, add it here as a boolean property.
 */
type ColumnCapabilities = {
  // Display capabilities
  isBoardCardLabelable: boolean
  isCopyable: boolean // This should only be false for columns in development

  // Organization capabilities
  isSortable: boolean
  isFilterable: boolean
  isGroupable: boolean
  isVerticalGroup: boolean
  isSliceable: boolean

  // Editing capabilities
  isValueClearable: boolean
  isValueEditable: boolean
  isValueBulkEditable: boolean
  isValueEditableFromFilter: boolean // Describes whether a column value can be set from a derived filter value when adding an item
  isPasteable: boolean

  // Action capabilities
  isValueActionable: boolean // This is an optional property for columns that can trigger actions like opening a PR

  // Describes whether the filter bar fetches async suggestions for this column
  hasRemoteFilterSuggestions: boolean

  // Describes which content types this column can be used with
  validContentTypes: ReadonlyArray<ItemType>
}

const AssigneeColumnCapabilities = {
  isBoardCardLabelable: false,
  isCopyable: true,
  isSortable: true,
  isFilterable: true,
  isGroupable: true,
  isVerticalGroup: false,
  isSliceable: true,
  isValueClearable: true,
  isValueEditable: true,
  isValueBulkEditable: true,
  isPasteable: true,
  isValueEditableFromFilter: true,
  hasRemoteFilterSuggestions: true,
  isValueActionable: false,
  validContentTypes: [ItemType.Issue, ItemType.PullRequest, ItemType.DraftIssue],
} as const

const DateColumnCapabilities = {
  isBoardCardLabelable: true,
  isCopyable: true,
  isSortable: true,
  isFilterable: true,
  isGroupable: true,
  isVerticalGroup: false,
  isSliceable: true,
  isValueClearable: true,
  isValueEditable: true,
  isValueEditableFromFilter: true,
  isPasteable: true,
  isValueBulkEditable: true,
  hasRemoteFilterSuggestions: true,
  isValueActionable: false,
  validContentTypes: [ItemType.Issue, ItemType.PullRequest, ItemType.DraftIssue, ItemType.AgentTask],
} as const

const IssueTypeColumnCapabilities = {
  isBoardCardLabelable: true,
  isCopyable: true,
  isSortable: true,
  isFilterable: true,
  isGroupable: true,
  isVerticalGroup: false,
  isSliceable: true,
  isValueClearable: true,
  isValueEditable: true,
  isValueActionable: false,
  isValueBulkEditable: true,
  isPasteable: true,
  isValueEditableFromFilter: true,
  hasRemoteFilterSuggestions: true,
  validContentTypes: [ItemType.Issue],
} as const

const IterationColumnCapabilities = {
  isBoardCardLabelable: true,
  isCopyable: true,
  isSortable: true,
  isFilterable: true,
  isGroupable: true,
  isVerticalGroup: true,
  isSliceable: true,
  isValueClearable: true,
  isValueEditable: true,
  isValueBulkEditable: true,
  isPasteable: true,
  isValueEditableFromFilter: true,
  hasRemoteFilterSuggestions: false,
  isValueActionable: false,
  validContentTypes: [ItemType.Issue, ItemType.PullRequest, ItemType.DraftIssue, ItemType.AgentTask],
} as const

const LabelColumnCapabilities = {
  isBoardCardLabelable: true,
  isCopyable: true,
  isSortable: true,
  isFilterable: true,
  isGroupable: false, // Grouping by Label name (regardless of repo) is supported on the server, but not the client
  isVerticalGroup: false,
  isSliceable: true,
  isValueClearable: true,
  isValueEditable: true,
  isPasteable: true,
  isValueBulkEditable: true,
  isValueEditableFromFilter: true,
  hasRemoteFilterSuggestions: true,
  isValueActionable: false,
  validContentTypes: [ItemType.Issue, ItemType.PullRequest],
} as const

const LinkedPullRequestsColumnCapabilities = {
  isBoardCardLabelable: true,
  isCopyable: true,
  isSortable: true,
  isFilterable: true,
  isGroupable: false,
  isVerticalGroup: false,
  isSliceable: false,
  isValueClearable: false,
  isValueEditable: false,
  isValueBulkEditable: false,
  isPasteable: false,
  isValueEditableFromFilter: false,
  hasRemoteFilterSuggestions: true,
  isValueActionable: true, // This column is actionable, meaning it can trigger actions like opening a PR
  validContentTypes: [ItemType.Issue, ItemType.PullRequest],
} as const

const MilestoneColumnCapabilities = {
  isBoardCardLabelable: true,
  isCopyable: true,
  isSortable: true,
  isFilterable: true,
  isGroupable: true,
  isVerticalGroup: false,
  isSliceable: true,
  isValueClearable: true,
  isValueEditable: true,
  isValueActionable: false,
  isValueBulkEditable: true,
  isPasteable: true,
  isValueEditableFromFilter: true,
  hasRemoteFilterSuggestions: true,
  validContentTypes: [ItemType.Issue, ItemType.PullRequest],
} as const

const NumberColumnCapabilities = {
  isBoardCardLabelable: true,
  isCopyable: true,
  isSortable: true,
  isFilterable: true,
  isGroupable: true,
  isVerticalGroup: false,
  isSliceable: true,
  isValueClearable: true,
  isValueEditable: true,
  isValueActionable: false,
  isValueBulkEditable: true,
  isPasteable: true,
  isValueEditableFromFilter: true,
  hasRemoteFilterSuggestions: true,
  validContentTypes: [ItemType.Issue, ItemType.PullRequest, ItemType.DraftIssue, ItemType.AgentTask],
} as const

const ParentIssueColumnCapabilities = {
  isBoardCardLabelable: true,
  isCopyable: true,
  isSortable: true,
  isFilterable: true,
  isGroupable: true,
  isVerticalGroup: false,
  isSliceable: true,
  isValueClearable: true,
  isValueEditable: true,
  isValueActionable: false,
  isValueBulkEditable: true,
  isPasteable: true,
  isValueEditableFromFilter: true,
  hasRemoteFilterSuggestions: true,
  validContentTypes: [ItemType.Issue],
} as const

const RepositoryColumnCapabilities = {
  isBoardCardLabelable: true,
  isCopyable: true,
  isSortable: true,
  isFilterable: true,
  isGroupable: true,
  isVerticalGroup: false,
  isSliceable: true,
  isValueClearable: false,
  isValueEditable: true,
  isValueActionable: true,
  isValueBulkEditable: false,
  isPasteable: false,
  isValueEditableFromFilter: false,
  hasRemoteFilterSuggestions: true,
  validContentTypes: [ItemType.Issue, ItemType.PullRequest],
} as const

const ReviewersColumnCapabilities = {
  isBoardCardLabelable: true,
  isCopyable: true,
  isSortable: true,
  isFilterable: true,
  isGroupable: false,
  isVerticalGroup: false,
  isSliceable: false,
  isValueClearable: false,
  isValueEditable: false,
  isValueActionable: false,
  isValueBulkEditable: false,
  isPasteable: false,
  isValueEditableFromFilter: false,
  hasRemoteFilterSuggestions: true,
  validContentTypes: [ItemType.PullRequest],
} as const

const SingleSelectColumnCapabilities = {
  isBoardCardLabelable: true,
  isCopyable: true,
  isSortable: true,
  isFilterable: true,
  isGroupable: true,
  isVerticalGroup: true,
  isSliceable: true,
  isValueClearable: true,
  isValueEditable: true,
  isValueActionable: false,
  isValueBulkEditable: true,
  isPasteable: true,
  isValueEditableFromFilter: true,
  hasRemoteFilterSuggestions: false,
  validContentTypes: [ItemType.Issue, ItemType.PullRequest, ItemType.DraftIssue, ItemType.AgentTask],
} as const

/**
 * Multi-select columns support filtering. Remaining organization capabilities
 * (sort/group/slice) can be enabled here as they are shipped.
 */
const MultiSelectColumnCapabilities = {
  isBoardCardLabelable: false,
  isCopyable: false,
  isSortable: false,
  isFilterable: true,
  isGroupable: false,
  isVerticalGroup: false,
  isSliceable: false,
  isValueClearable: true,
  isValueEditable: true,
  isValueActionable: false,
  isValueBulkEditable: false,
  isPasteable: false,
  isValueEditableFromFilter: false,
  hasRemoteFilterSuggestions: false,
  validContentTypes: [ItemType.Issue, ItemType.PullRequest, ItemType.DraftIssue, ItemType.AgentTask],
} as const

const SubIssuesProgressColumnCapabilities = {
  isBoardCardLabelable: true,
  isCopyable: true,
  isSortable: true,
  isFilterable: true,
  isGroupable: false,
  isVerticalGroup: false,
  isSliceable: false,
  isValueClearable: false,
  isValueEditable: false,
  isValueActionable: false,
  isValueBulkEditable: false,
  isPasteable: false,
  isValueEditableFromFilter: false,
  hasRemoteFilterSuggestions: false,
  validContentTypes: [ItemType.Issue],
} as const

const TextColumnCapabilities = {
  isBoardCardLabelable: true,
  isCopyable: true,
  isSortable: true,
  isFilterable: true,
  isGroupable: true,
  isVerticalGroup: false,
  isSliceable: true,
  isValueClearable: true,
  isValueEditable: true,
  isValueActionable: false,
  isValueBulkEditable: true,
  isPasteable: true,
  isValueEditableFromFilter: true,
  hasRemoteFilterSuggestions: true,
  validContentTypes: [ItemType.Issue, ItemType.PullRequest, ItemType.DraftIssue, ItemType.AgentTask],
} as const

const TitleColumnCapabilities = {
  isBoardCardLabelable: false,
  isCopyable: true,
  isSortable: true,
  isFilterable: true,
  isGroupable: false,
  isVerticalGroup: false,
  isSliceable: false,
  isValueClearable: false,
  isValueEditable: true,
  isValueActionable: false,
  isPasteable: false,
  isValueBulkEditable: false,
  isValueEditableFromFilter: false,
  hasRemoteFilterSuggestions: false,
  validContentTypes: [ItemType.Issue, ItemType.PullRequest, ItemType.DraftIssue],
} as const

// For the time being, all timestamp fields are read-only system fields, so all editing capabilities are set to false.
const TimestampColumnCapabilities = {
  isBoardCardLabelable: true,
  isCopyable: true,
  isSortable: true,
  isFilterable: true,
  isGroupable: false,
  isVerticalGroup: false,
  isSliceable: false,
  isValueClearable: false,
  isValueEditable: false,
  isValueActionable: false,
  isPasteable: false,
  isValueBulkEditable: false,
  isValueEditableFromFilter: false,
  hasRemoteFilterSuggestions: false,
  validContentTypes: [ItemType.Issue, ItemType.PullRequest, ItemType.DraftIssue],
} as const

/** Closed has the same base capabilities but does not apply to draft issues */
const ClosedColumnCapabilities = {
  ...TimestampColumnCapabilities,
  validContentTypes: [ItemType.Issue, ItemType.PullRequest],
} as const
const CreatedColumnCapabilities = TimestampColumnCapabilities
const UpdatedColumnCapabilities = TimestampColumnCapabilities

/**
 * Maps each column type to its capabilities.
 * When adding a new column type to the system, add an entry here
 * with its appropriate capabilities.
 */
const columnCapabilitiesMap: {
  [key in MemexColumnDataType]: ColumnCapabilities
} = {
  [MemexColumnDataType.Assignees]: AssigneeColumnCapabilities,
  [MemexColumnDataType.Closed]: ClosedColumnCapabilities,
  [MemexColumnDataType.Created]: CreatedColumnCapabilities,
  [MemexColumnDataType.Date]: DateColumnCapabilities,
  [MemexColumnDataType.IssueType]: IssueTypeColumnCapabilities,
  [MemexColumnDataType.Iteration]: IterationColumnCapabilities,
  [MemexColumnDataType.Labels]: LabelColumnCapabilities,
  [MemexColumnDataType.LinkedPullRequests]: LinkedPullRequestsColumnCapabilities,
  [MemexColumnDataType.Milestone]: MilestoneColumnCapabilities,
  [MemexColumnDataType.Number]: NumberColumnCapabilities,
  [MemexColumnDataType.ParentIssue]: ParentIssueColumnCapabilities,
  [MemexColumnDataType.Repository]: RepositoryColumnCapabilities,
  [MemexColumnDataType.Reviewers]: ReviewersColumnCapabilities,
  [MemexColumnDataType.SingleSelect]: SingleSelectColumnCapabilities,
  [MemexColumnDataType.MultiSelect]: MultiSelectColumnCapabilities,
  [MemexColumnDataType.SubIssuesProgress]: SubIssuesProgressColumnCapabilities,
  [MemexColumnDataType.Text]: TextColumnCapabilities,
  [MemexColumnDataType.Title]: TitleColumnCapabilities,
  [MemexColumnDataType.Updated]: UpdatedColumnCapabilities,
}

function getCapableColumns(prop: keyof ColumnCapabilities): Array<MemexColumnDataType> {
  return (Object.keys(columnCapabilitiesMap) as Array<MemexColumnDataType>).filter(
    key => columnCapabilitiesMap[key][prop],
  )
}

function getColumnsForContent(contentType: ItemType): Array<MemexColumnDataType> {
  return (Object.keys(columnCapabilitiesMap) as Array<MemexColumnDataType>).filter(key =>
    columnCapabilitiesMap[key].validContentTypes.includes(contentType),
  )
}

/**
 * Explicit type mapping for all column capabilities.
 * This is technically redundant with columnCapabilitiesMap, but provides
 * stronger compile-time type checking and enables type extraction utilities.
 *
 * When adding a new column type, you must add it here to keep the types in sync.
 */
type ColumnCapabilitiesType = {
  [MemexColumnDataType.Assignees]: typeof AssigneeColumnCapabilities
  [MemexColumnDataType.Closed]: typeof ClosedColumnCapabilities
  [MemexColumnDataType.Created]: typeof CreatedColumnCapabilities
  [MemexColumnDataType.Date]: typeof DateColumnCapabilities
  [MemexColumnDataType.IssueType]: typeof IssueTypeColumnCapabilities
  [MemexColumnDataType.Iteration]: typeof IterationColumnCapabilities
  [MemexColumnDataType.Labels]: typeof LabelColumnCapabilities
  [MemexColumnDataType.LinkedPullRequests]: typeof LinkedPullRequestsColumnCapabilities
  [MemexColumnDataType.Milestone]: typeof MilestoneColumnCapabilities
  [MemexColumnDataType.Number]: typeof NumberColumnCapabilities
  [MemexColumnDataType.ParentIssue]: typeof ParentIssueColumnCapabilities
  [MemexColumnDataType.Repository]: typeof RepositoryColumnCapabilities
  [MemexColumnDataType.Reviewers]: typeof ReviewersColumnCapabilities
  [MemexColumnDataType.SingleSelect]: typeof SingleSelectColumnCapabilities
  [MemexColumnDataType.MultiSelect]: typeof MultiSelectColumnCapabilities
  [MemexColumnDataType.SubIssuesProgress]: typeof SubIssuesProgressColumnCapabilities
  [MemexColumnDataType.Text]: typeof TextColumnCapabilities
  [MemexColumnDataType.Title]: typeof TitleColumnCapabilities
  [MemexColumnDataType.Updated]: typeof UpdatedColumnCapabilities
}

type ExtractColumnCapabilityType<Property extends keyof ColumnCapabilitiesType[keyof ColumnCapabilitiesType]> = {
  [K in keyof ColumnCapabilitiesType]: ColumnCapabilitiesType[K][Property] extends true ? K : never
}[keyof ColumnCapabilitiesType]

type ExtractColumnTypesForItemType<Target extends ItemType> = {
  [K in keyof ColumnCapabilitiesType]: ColumnCapabilitiesType[K]['validContentTypes'] extends ReadonlyArray<infer U>
    ? Target extends U
      ? K
      : never
    : never
}[keyof ColumnCapabilitiesType]

// Board card rendering

const boardLabelableColumns = getCapableColumns('isBoardCardLabelable')

type BoardLabelableColumn = ExtractColumnCapabilityType<'isBoardCardLabelable'>

export function isBoardLabelableColumn(column: MemexColumnDataType): column is BoardLabelableColumn {
  return boardLabelableColumns.includes(column)
}

// Sorting

const sortableColumns = getCapableColumns('isSortable')

type SortableColumn = ExtractColumnCapabilityType<'isSortable'>

export function isSortableColumn(column: MemexColumnDataType): column is SortableColumn {
  return sortableColumns.includes(column)
}

// Filtering

const filterableColumns = getCapableColumns('isFilterable')

type FilterableColumn = ExtractColumnCapabilityType<'isFilterable'>

export function isFilterableColumn(column: MemexColumnDataType): column is FilterableColumn {
  return filterableColumns.includes(column)
}

// Grouping

const groupableColumns = getCapableColumns('isGroupable')

export type GroupableColumn = ExtractColumnCapabilityType<'isGroupable'>

export function isGroupableColumn(column: MemexColumnDataType): column is GroupableColumn {
  return groupableColumns.includes(column)
}

const verticalGroupColumns = getCapableColumns('isVerticalGroup')

export type VerticalGroupColumn = ExtractColumnCapabilityType<'isVerticalGroup'>

export function isVerticalGroupColumn(column: MemexColumnDataType): column is VerticalGroupColumn {
  return verticalGroupColumns.includes(column)
}

// Slicing

const slicableColumns = getCapableColumns('isSliceable')

export type SlicableColumn = ExtractColumnCapabilityType<'isSliceable'>

export function isSliceableColumn(column: MemexColumnDataType): column is SlicableColumn {
  return slicableColumns.includes(column)
}

export type GroupableMetadataType = GroupableColumn | SlicableColumn

export function isGroupMetadataType(column: MemexColumnDataType): column is GroupableMetadataType {
  return isGroupableColumn(column) || slicableColumns.includes(column)
}

// Editing

const valueEditableColumns = getCapableColumns('isValueEditable')

export type ValueEditableColumn = ExtractColumnCapabilityType<'isValueEditable'>

export function isValueEditableColumn(column: MemexColumnDataType): column is ValueEditableColumn {
  return valueEditableColumns.includes(column)
}

// Take Action
const valueActionableColumns = getCapableColumns('isValueActionable')

export type ValueActionableColumn = ExtractColumnCapabilityType<'isValueActionable'>

export function isValueActionableColumn(column: MemexColumnDataType): column is ValueActionableColumn {
  return valueActionableColumns.includes(column)
}

const bulkValueEditableColumns = getCapableColumns('isValueBulkEditable')

export type BulkValueEditableColumn = ExtractColumnCapabilityType<'isValueBulkEditable'>

export function isValueBulkEditableColumn(column: MemexColumnDataType): column is ValueEditableColumn {
  return bulkValueEditableColumns.includes(column)
}

const valueClearableColumns = getCapableColumns('isValueClearable')

type ClearableColumn = ExtractColumnCapabilityType<'isValueClearable'>

export function isValueClearableColumn(column: MemexColumnDataType): column is ClearableColumn {
  return valueClearableColumns.includes(column)
}

const valueEditableFromFilterColumns = getCapableColumns('isValueEditableFromFilter')

export type ValueEditableFromFilterColumn = ExtractColumnCapabilityType<'isValueEditableFromFilter'>

export function isValueEditableFromFilterColumn(column: MemexColumnDataType): column is ValueEditableFromFilterColumn {
  return valueEditableFromFilterColumns.includes(column)
}

// Remote filter suggestions (does the filter-bar fetch async suggestions)

const remoteFilterSuggestionsColumn = getCapableColumns('hasRemoteFilterSuggestions')

type RemoteFilterSuggestionsColumn = ExtractColumnCapabilityType<'hasRemoteFilterSuggestions'>

export function isRemoteFilterSuggestionsColumn(column: MemexColumnDataType): column is RemoteFilterSuggestionsColumn {
  return remoteFilterSuggestionsColumn.includes(column)
}

const copyableColumns = getCapableColumns('isCopyable')

export type CopyableColumn = ExtractColumnCapabilityType<'isCopyable'>

export function isCopyableColumn(column: MemexColumnDataType): column is CopyableColumn {
  return copyableColumns.includes(column)
}

const pasteableColumns = getCapableColumns('isPasteable')

export type PasteableColumn = ExtractColumnCapabilityType<'isPasteable'>

export function isPasteableColumn(column: MemexColumnDataType): column is PasteableColumn {
  return pasteableColumns.includes(column)
}

// Columns supporting draft issues

const draftIssueColumns = getColumnsForContent(ItemType.DraftIssue)

export type DraftIssueColumns = ExtractColumnTypesForItemType<typeof ItemType.DraftIssue>

export function isDraftIssueColumn(column: MemexColumnDataType): column is DraftIssueColumns {
  return draftIssueColumns.includes(column)
}

// Columns supporting pull requests

const PullRequestColumns = getColumnsForContent(ItemType.PullRequest)

export type PullRequestColumns = ExtractColumnTypesForItemType<typeof ItemType.PullRequest>

export function isPullRequestColumn(column: MemexColumnDataType): column is PullRequestColumns {
  return PullRequestColumns.includes(column)
}

// Columns supporting issues

const issueColumns = getColumnsForContent(ItemType.Issue)

export type IssueColumns = ExtractColumnTypesForItemType<typeof ItemType.Issue>

export function isIssueColumn(column: MemexColumnDataType): column is IssueColumns {
  return issueColumns.includes(column)
}

// Columns that exclusively support issues

export type IssuesOnlyColumns = Exclude<IssueColumns, DraftIssueColumns | PullRequestColumns | AgentTaskColumns>

export function isIssuesOnlyColumn(column: MemexColumnDataType): column is IssuesOnlyColumns {
  return (
    isIssueColumn(column) && !isDraftIssueColumn(column) && !isPullRequestColumn(column) && !isAgentTaskColumn(column)
  )
}

// Columns supporting agent tasks

const agentTaskColumns = getColumnsForContent(ItemType.AgentTask)

export type AgentTaskColumns = ExtractColumnTypesForItemType<typeof ItemType.AgentTask>

export function isAgentTaskColumn(column: MemexColumnDataType): column is AgentTaskColumns {
  return agentTaskColumns.includes(column)
}

// Type assertions to ensure custom columns support all item types
//
// We need these assertions because some validation checks (like isValidDraftItemColumn)
// only examine column IDs stored on a column value without accessing the column's dataType. This avoids excessive lookups.
//
// The assertions verify that numeric IDs (custom columns) work with all item types.
// If a custom column type ever needs to be restricted from certain item types,
// we'd need to refactor these checks to look up the column by ID and examine its dataType.
type Assert<T extends true> = T

export type _AssertDraftIssueSupport = Assert<
  CustomColumnKind extends DraftIssueColumns ? true : 'Custom columns cannot be disabled for Draft Issues'
>
export type _AssertIssueSupport = Assert<
  CustomColumnKind extends IssueColumns ? true : 'Custom columns cannot be disabled for Issues'
>
export type _AssertPullRequestSupport = Assert<
  CustomColumnKind extends PullRequestColumns ? true : 'Custom columns cannot be disabled for Pull Requests'
>
export type _AssertAgentTaskSupport = Assert<
  CustomColumnKind extends AgentTaskColumns ? true : 'Custom columns cannot be disabled for Agent Tasks'
>
